import { Head } from "@inertiajs/react";
import CtaBand from "@/components/marketing/cta-band";
import PageHero from "@/components/marketing/page-hero";
import SectionHeading from "@/components/marketing/section-heading";
import SolutionDuo from "@/components/marketing/solution-duo";
import PublicLayout from "@/layouts/public-layout";
import { siteLinks, solutionSummaries } from "@/data/marketing-site";

const highlights = [
  {
    title: "Over 6.000 danske virksomheder er omfattet",
    copy: "NIS2 udvider markant fra den tidligere NIS-lov. Energi, transport, sundhed, finans, digital infrastruktur og 13 andre sektorer er nu underlagt krav om aktiv cybersikkerhedsstyring."
  },
  {
    title: "Forsyningskæden rammer også de små",
    copy: "Er I leverandør til en virksomhed, der er NIS2-omfattet, vil de kræve dokumentation for jeres sikkerhedsniveau. Omfanget afgøres ikke kun af jeres størrelse — men af hvem I leverer til."
  },
  {
    title: "Det handler ikke om at forstå loven — men om at bevise den",
    copy: "NIS2 kræver operationelle processer, løbende kontroller, evidens og aktiv ledelsesopfølgning i praksis. En politik i et Word-dokument er ikke nok."
  }
];

const requirementAreas = [
  {
    icon: "bi bi-diagram-3",
    title: "Governance & ledelsesansvar",
    copy: "NIS2 gør ledelsesansvaret personligt. Direktion og bestyrelse skal kunne dokumentere, at de aktivt godkender retning og fører tilsyn med cybersikkerheden. Obsero samler alle politikker og strategiske mål ét sted i ServiceNow og udsender automatisk opgaver til ledelsen — så aktivt ansvar ikke er noget, man erklærer, men noget, man beviser."
  },
  {
    icon: "bi bi-speedometer2",
    title: "Risikostyring",
    copy: "Risici skal ikke bare identificeres — de skal vurderes, behandles og følges op løbende. Obsero kobler jeres kritiske IT-aktiver direkte til de trusler, de er udsat for, og giver et realtids-risikobillede af hele forretningen. Altid opdateret. Altid dokumenterbart."
  },
  {
    icon: "bi bi-broadcast",
    title: "Hændelseshåndtering og rapportering",
    copy: "NIS2 kræver, at væsentlige hændelser rapporteres til myndighederne inden for 24 timer. Obsero automatiserer hele Incident Response — systemet ved præcis, hvem der skal have besked, hvad der skal indsamles og hvornår. Ingen manuel koordinering under pres."
  },
  {
    icon: "bi bi-buildings",
    title: "Forsyningskædesikkerhed",
    copy: "Kritiske leverandører skal risikovurderes og følges aktivt op. Obsero automatiserer overvågningen af jeres tredjeparter — sender spørgeskemaer ud, tjekker certifikater og scorer sikkerhedsniveauet. Forsyningskæden er ikke et blindt punkt. Den er en del af systemet."
  }
];

const coverageChecks = [
  {
    title: "Sektor og samfundskritisk rolle",
    copy: "Energi, transport, sundhed, finans, digital infrastruktur, drikkevand, fødevarer, offentlig administration, post- og pakkeservice, affaldshåndtering, rummet, elektroniske kommunikationstjenester og fremstilling af kritiske produkter som medicin, medicinsk udstyr og kemikalier."
  },
  {
    title: "Størrelse: 50 ansatte eller 10 mio. EUR",
    copy: "Er I over 50 ansatte eller har en årlig omsætning over 10 mio. EUR — og opererer i en omfattet sektor — er I direkte underlagt NIS2. Begge kriterier skal vurderes for hvert CVR-nummer."
  },
  {
    title: "Leverandør til de omfattede",
    copy: "Selv hvis I ikke er direkte omfattet, kan krav komme via jeres kunder. NIS2-virksomheder er forpligtet til at sikre hele deres forsyningskæde — og vil kræve dokumentation fra jer som leverandør."
  }
];

const consequenceRows = [
  {
    icon: "bi bi-cash-coin",
    title: "Bøder på op til millioner af euro"
  },
  {
    icon: "bi bi-person-badge",
    title: "Personligt ansvar for bestyrelse og direktion"
  },
  {
    icon: "bi bi-clipboard-check",
    title: "Tilsyn og påbud fra dag ét",
    copy: ""
  },
  {
    icon: "bi bi-file-earmark-lock",
    title: "Manglende compliance koster kunder og kontrakter"
  }
];

function NIS2() {
  return (
    <>
      <Head title="NIS2">
        <meta
          head-key="description"
          name="description"
          content="NIS2 stiller krav til ledelsesansvar, risikostyring, hændelseshåndtering og forsyningskædesikkerhed. Se hvordan Obsero hjælper jer i mål."
        />
      </Head>

      <PageHero
        narrow
        size="compact"
        title="NIS2 har ramt alle virksomheder i Danmark – men hvad betyder det egentlig?"
        copy="NIS2 trådte i kraft i Danmark 1. juli 2025 — og myndighederne stiller krav om at se operationelle processer, løbende kontroller og aktiv ledelsesopfølgning. Har I styr på det — eller skal Obsero levere systemet?"
        actions={[
          { href: siteLinks.demo, label: "Book møde", className: "btn btn-light" }
        ]}
      />

      <section className="public-section public-section--white-surface">
        <div className="public-container">
          <div className="public-split-callout">
            <div className="public-split-callout__lead public-split-callout__lead--accented">
              <SectionHeading
                title="Lovgivningen rammer bredere end de fleste tror."
              />

              <div className="public-copy-stack">
                <p className="public-section__copy">
                  Hvor den gamle NIS-lov berørte cirka 1.000 danske virksomheder, er over 6.000 nu omfattet af NIS2 — på tværs af 18 sektorer. Og selv hvis I ikke er direkte omfattet, kan jeres kunder kræve, at I kan dokumentere jeres sikkerhed som led i deres forsyningskæde.
                </p>
                <p className="public-section__copy">
                  Det stiller krav til styring, dokumentation og ledelsesansvar på et niveau, de færreste har været vant til.
                </p>
              </div>
            </div>

            <div className="public-stack-list">
              {highlights.map((item, index) => (
                <article key={item.title} className="public-stack-item">
                  <div className="public-stack-item__index">{String(index + 1).padStart(2, "0")}</div>
                  <div className="public-stack-item__body">
                    <h3 className="public-stack-item__title">{item.title}</h3>
                    <p className="public-stack-item__copy">{item.copy}</p>
                  </div>
                </article>
              ))}
            </div>
          </div>

        </div>
      </section>

      <section className="public-section public-section--shell-band">
        <div className="public-container">
          <SectionHeading
            eyebrow="Hvem er omfattet af NIS2?"
            title="Tre kriterier afgør, om I er omfattet."
          />

          <div className="public-pillars public-pillars--spacious">
            {coverageChecks.map((item, index) => {
              const icons = ["bi bi-building", "bi bi-people", "bi bi-link-45deg"];

              return (
                <article key={item.title} className="public-pillar">
                  <div className="public-icon-chip">
                    <i className={icons[index]} aria-hidden="true" />
                  </div>
                  <div className="public-pillar__body">
                    <h3 className="public-pillar__title">{item.title}</h3>
                    <p className="public-pillar__copy">{item.copy}</p>
                  </div>
                </article>
              );
            })}
          </div>

        </div>
      </section>

      <section className="public-section public-section--white-band" id="krav">
        <div className="public-container">
          <SectionHeading
            title="NIS2 stiller krav inden for fire områder."
            copy="Lovkravet kan ikke løses med enkeltstående politikker. Governance, risiko, hændelser og leverandørstyring skal kunne fungere samlet og være dokumenterbare under tilsyn."
          />

          <div className="public-module-rail">
            {requirementAreas.map((item, index) => (
              <article key={item.title} className="public-module-row">
                <div className="public-module-row__index">{String(index + 1).padStart(2, "0")}</div>
                <div className="public-module-row__body">
                  <h3 className="public-module-row__title">{item.title}</h3>
                  <p className="public-module-row__copy">{item.copy}</p>
                </div>
                <div className="public-icon-chip" aria-hidden="true">
                  <i className={item.icon} />
                </div>
              </article>
            ))}
          </div>

        </div>
      </section>

      <section className="public-section public-section--solution-band" id="loesninger">
        <div className="public-container">
          <SectionHeading
            title="Sådan kan Obsero hjælpe jer med NIS2."
          />

          <SolutionDuo
            items={solutionSummaries.map((solution) => ({
              key: solution.key,
              title: solution.label,
              copy: solution.copy,
              fit: solution.key === "core"
                ? ["NIS2-compliant på kun 12 uger.", ...solution.fit]
                : solution.fit,
              href: solution.href
            }))}
          />
        </div>
      </section>

      <section className="public-section public-section--white-band">
        <div className="public-container">
          <div className="public-split-callout">
            <div className="public-split-callout__lead">
              <SectionHeading
                title="NIS2 er ikke et papirkrav."
                copy="Styrelsen for Samfundssikkerhed kan føre tilsyn og udstede påbud fra dag ét. Når ledelsesansvar, tilsyn og markedsforventninger skærpes samtidig, bliver det dyrt at stå med uafklarede roller eller svag dokumentation."
              />
            </div>

            <div className="public-stack-list">
              {consequenceRows.map((item, index) => (
                <article key={item.title} className="public-stack-item">
                  <div className="public-stack-item__index">{String(index + 1).padStart(2, "0")}</div>
                  <div className="public-stack-item__body">
                    <h3 className="public-stack-item__title">{item.title}</h3>
                  </div>
                </article>
              ))}
            </div>
          </div>
        </div>
      </section>

      <CtaBand
        eyebrow="NIS2 i praksis"
        title="Er I klar til at tage det næste skridt?"
        copy="Vi gennemgår gerne løsningen, viser de centrale artefakter og gør det tydeligt, hvordan I kommer fra lovkrav til dokumenterbar compliance i drift."
        actions={[
          { href: siteLinks.contact, label: "Kontakt os", className: "btn btn-light" }
        ]}
      />
    </>
  );
}

NIS2.layout = (page) => <PublicLayout>{page}</PublicLayout>;

export default NIS2;
